// Same boarding-pass-style e-ticket design as the Maxit mini-app's
// TripDetails.ts (~/Desktop/afritrip-oui-designer), rebuilt against the
// real FlightHub Trip Details response instead of mock data. This runs in
// a normal browser, so it can use canvas/Image/doc.save() directly — none
// of the mini-app's native-bridge workarounds are needed here.
import QRCode from 'qrcode';
import { tr, currentLang } from './i18n';
import { buildLegs, buildPassengers, buildPrice, formatMoney, buildBaggage, buildConditions, baggageStatusLabel, conditionStatusLabel } from './tripFormat';

const IMG = process.env.PUBLIC_URL + '/assets/AfriTrip Landing Page_img';
const LOGO_URL = `${IMG}/LOGO-FLYAFRITRIP 1-2.png`;

const BRAND = [253, 102, 2];
const DARK = [29, 41, 57];
const BODY = [52, 64, 84];
const MUTED = [102, 112, 133];
const LINE = [234, 236, 240];
const PANEL = [242, 244, 247];
const GOOD = [3, 127, 76];

function loadImageAsDataUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/png'), width: img.naturalWidth, height: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = url;
  });
}

// Deep link the QR encodes — opens flyafritrip.com/booking pre-filled with
// this booking's reference + last name, so scanning it actually retrieves
// the ticket instead of pretending to be an airport gate scan (which no
// code we generate could ever be — that requires the airline's own
// check-in/DCS system, not FlightHub or us).
const SITE_ORIGIN = 'https://flyafritrip.com';

export function bookingLookupUrl(referenceNumber, lastName) {
  const params = new URLSearchParams({ ref: referenceNumber ?? '', last: lastName ?? '' });
  return `${SITE_ORIGIN}/booking?${params.toString()}`;
}

export async function downloadTicketPdf({ tripDetails, transactionSummary, fareRules }) {
  const lookupUrl = bookingLookupUrl(tripDetails.reference_number, tripDetails.primary_contact?.last_name);
  const [{ jsPDF }, logo, qrDataUrl] = await Promise.all([
    import('jspdf'),
    loadImageAsDataUrl(LOGO_URL),
    QRCode.toDataURL(lookupUrl, { margin: 0, color: { dark: '#1D2939', light: '#0000' } })
  ]);

  const booking = tripDetails.bookings?.[0] ?? {};
  const legs = buildLegs(booking, tripDetails.carriers, tripDetails.airports);
  const passengers = buildPassengers(booking);
  const baggage = buildBaggage(booking, fareRules);
  const conditions = buildConditions(fareRules);
  const { total, currency, seatFees } = buildPrice(booking, transactionSummary);

  const PAGE_W = 210;
  const MARGIN = 15;
  const CONTENT_W = PAGE_W - MARGIN * 2;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });

  const card = (x, top, w, h, fill) => {
    if (fill) {
      doc.setFillColor(...fill);
      doc.roundedRect(x, top, w, h, 2.5, 2.5, 'F');
    }
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, top, w, h, 2.5, 2.5, 'S');
  };
  const perforation = (x1, x2, atY) => {
    doc.setDrawColor(...LINE);
    doc.setLineDashPattern([1.2, 1.2], 0);
    doc.line(x1, atY, x2, atY);
    doc.setLineDashPattern([], 0);
    doc.setFillColor(255, 255, 255);
    doc.circle(x1, atY, 2.2, 'F');
    doc.circle(x2, atY, 2.2, 'F');
    doc.setDrawColor(...LINE);
    doc.circle(x1, atY, 2.2, 'S');
    doc.circle(x2, atY, 2.2, 'S');
  };

  // ---- Header ----
  doc.setFillColor(...BRAND);
  doc.rect(0, 0, PAGE_W, 3, 'F');

  // Booking confirmation and ticket issuance are two separate, real stages
  // (flagged by the client 2026-10-02) — FlightHub confirms the booking/PNR
  // well before the ticket itself is cut. Showing the raw status here used
  // to print "NOT_ISSUED" inside a green badge, which reads as a confirmed
  // ticket when nothing has actually been issued yet.
  const ticketIssued = tripDetails.status === 'issued';
  const cancelled = /^cancel+ed$/i.test(tripDetails.status || '');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(cancelled ? tr('pdfLabelCancelled') : ticketIssued ? tr('pdfLabelIssued') : tr('pdfLabelConfirmed'), MARGIN + CONTENT_W, 13, { align: 'right' });
  doc.setFillColor(...(cancelled ? [180, 35, 24] : ticketIssued ? GOOD : BRAND));
  const fr = currentLang() === 'fr';
  const badgeW = cancelled ? (fr ? 42 : 38) : ticketIssued ? 26 : (fr ? 46 : 36);
  doc.roundedRect(MARGIN + CONTENT_W - badgeW, 16, badgeW, 6.5, 3.25, 3.25, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.text(cancelled ? tr('badgeCancelled') : ticketIssued ? tr('badgeIssued') : tr('badgeConfirmed'), MARGIN + CONTENT_W - badgeW / 2, 20.4, { align: 'center' });

  const logoW = 60;
  const logoH = (logo.height / logo.width) * logoW;
  doc.addImage(logo.dataUrl, 'PNG', (PAGE_W - logoW) / 2, 9, logoW, logoH);

  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.3);
  doc.line(MARGIN, 32, MARGIN + CONTENT_W, 32);

  let y = 41;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...DARK);
  doc.text(`${tr('pdfTripTo')} ${tripDetails.destination_airport_code}`, MARGIN, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...BODY);
  doc.text(`${tr('pdfRef')}  ${tripDetails.reference_number}`, MARGIN, y);
  y += 5.5;
  if (tripDetails.primary_contact?.phone) {
    doc.text(`${tr('pdfPhone')}  ${tripDetails.primary_contact.phone}`, MARGIN, y);
    y += 5;
  }
  doc.setTextColor(...MUTED);
  doc.setFontSize(8.5);
  doc.text(`${tr('pdfSentTo')} ${tripDetails.primary_contact?.email || ''}`, MARGIN, y);
  y += 9;

  // ---- One boarding-pass card per flight leg ----
  for (const leg of legs) {
    const boxTop = y;
    // 32 (down to the perforation) + 16 (QR) + 3 (gap) + ~4 (label) + 3 (bottom padding)
    // — taller than the old 44 since a real QR needs more room than the
    // thin decorative barcode strip it replaced.
    const boxH = 58;
    card(MARGIN, boxTop, CONTENT_W, boxH);

    doc.setFillColor(...PANEL);
    doc.roundedRect(MARGIN, boxTop, CONTENT_W, 9, 2.5, 2.5, 'F');
    doc.rect(MARGIN, boxTop + 4.5, CONTENT_W, 4.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...BRAND);
    doc.text(leg.label.toUpperCase(), MARGIN + 5, boxTop + 6);
    doc.setTextColor(...MUTED);
    doc.text(leg.dateLabel, MARGIN + CONTENT_W - 5, boxTop + 6, { align: 'right' });

    const routeY = boxTop + 20;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(...DARK);
    doc.text(leg.from, MARGIN + 5, routeY);
    doc.text(leg.to, MARGIN + 62, routeY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...BODY);
    doc.text(leg.departureTime, MARGIN + 5, routeY + 5.5);
    doc.text(leg.arrivalTime, MARGIN + 62, routeY + 5.5);
    // Full city and country under the times; shrink to fit the column.
    doc.setTextColor(...MUTED);
    const place = (text, x, maxW) => {
      if (!text) return;
      let size = 7.5;
      doc.setFontSize(size);
      while (size > 5.5 && doc.getTextWidth(text) > maxW) {
        size -= 0.25;
        doc.setFontSize(size);
      }
      doc.text(text, x, routeY + 9.8);
    };
    place(leg.fromPlace, MARGIN + 5, 52);
    place(leg.toPlace, MARGIN + 62, 34);

    const lineY = routeY - 2;
    const lineX1 = MARGIN + 26;
    const lineX2 = MARGIN + 59;
    doc.setDrawColor(...BRAND);
    doc.setLineWidth(0.6);
    doc.line(lineX1, lineY, lineX2, lineY);
    doc.setFillColor(...BRAND);
    doc.circle(lineX1, lineY, 0.9, 'F');
    doc.circle(lineX2, lineY, 0.9, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(leg.duration, (lineX1 + lineX2) / 2, lineY - 2, { align: 'center' });
    doc.text(leg.stopsLabel, (lineX1 + lineX2) / 2, lineY + 4, { align: 'center' });

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(leg.airline, MARGIN + 98, routeY - 1);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`${tr('cabinLine')} ${leg.cabin}`, MARGIN + 98, routeY + 4.5);

    const perfY = boxTop + 32;
    perforation(MARGIN, MARGIN + CONTENT_W, perfY);

    const qrSize = 16;
    const qrX = MARGIN + CONTENT_W - 5 - qrSize;
    doc.addImage(qrDataUrl, 'PNG', qrX, perfY + 2, qrSize, qrSize);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(tr('scan'), qrX + qrSize / 2, perfY + 2 + qrSize + 3, { align: 'center' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(tr('stubRef'), MARGIN + 5, perfY + 5);
    if (leg.pnr) doc.text(tr('stubPnr'), MARGIN + 50, perfY + 5);
    doc.text(tr('stubClass'), MARGIN + 88, perfY + 5);
    if (leg.seatsText) doc.text(tr('stubSeat'), MARGIN + 112, perfY + 5);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...DARK);
    doc.text(tripDetails.reference_number, MARGIN + 5, perfY + 9.5);
    if (leg.pnr) doc.text(leg.pnr, MARGIN + 50, perfY + 9.5);
    doc.text(leg.cabin, MARGIN + 88, perfY + 9.5);
    if (leg.seatsText) {
      doc.setFontSize(8.5);
      doc.text(doc.splitTextToSize(leg.seatsText, 36).slice(0, 2), MARGIN + 112, perfY + 9.5);
      doc.setFontSize(9);
    }

    y = boxTop + boxH + 7;
  }

  // ---- Passengers ----
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text(tr('passengers'), MARGIN, y);
  y += 6;

  const tableTop = y;
  doc.setFillColor(...PANEL);
  doc.roundedRect(MARGIN, tableTop, CONTENT_W, 7, 1.5, 1.5, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(tr('pdfColName'), MARGIN + 4, tableTop + 4.7);
  if (ticketIssued) doc.text(tr('pdfColTicket'), MARGIN + 92, tableTop + 4.7);
  y = tableTop + 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  passengers.forEach((traveller, i) => {
    const rowH = 8.5;
    if (i % 2 === 1) {
      doc.setFillColor(...PANEL);
      doc.rect(MARGIN, y, CONTENT_W, rowH, 'F');
    }
    doc.setTextColor(...DARK);
    doc.text(traveller.name, MARGIN + 4, y + 5.5);
    doc.setTextColor(...BODY);
    if (ticketIssued) doc.text(traveller.eTicket, MARGIN + 92, y + 5.5);
    y += rowH;
  });
  doc.setDrawColor(...LINE);
  doc.roundedRect(MARGIN, tableTop, CONTENT_W, y - tableTop, 1.5, 1.5, 'S');
  y += 9;

  // Content can now run past one page (baggage + fare conditions), so every
  // block asks for the room it needs first and starts a new page if short.
  const PAGE_BOTTOM = 266;
  const ensureSpace = (h) => {
    if (y + h > PAGE_BOTTOM) {
      doc.addPage();
      y = 18;
    }
  };

  // ---- Baggage allowance ----
  ensureSpace(30);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text(tr('bagTitle'), MARGIN, y);
  y += 6;
  if (baggage.any) {
    const cols = [
      { x: MARGIN + 4, label: tr('bagLeg') },
      { x: MARGIN + 36, label: tr('bagPersonal') },
      { x: MARGIN + 68, label: tr('bagCabin') },
      { x: MARGIN + 100, label: tr('bagChecked') },
      { x: MARGIN + 141, label: tr('bagChecked2') }
    ];
    const bagTop = y;
    doc.setFillColor(...PANEL);
    doc.roundedRect(MARGIN, bagTop, CONTENT_W, 7, 1.5, 1.5, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    cols.forEach((c) => doc.text(c.label.toUpperCase(), c.x, bagTop + 4.7));
    y = bagTop + 7;
    const statusColor = (st) => (st === 'inc' ? GOOD : st === 'cha' ? [181, 71, 8] : st === 'nof' ? [180, 35, 24] : BODY);
    baggage.rows.forEach((r, i) => {
      const rowH = 8.5;
      if (i % 2 === 1) {
        doc.setFillColor(...PANEL);
        doc.rect(MARGIN, y, CONTENT_W, rowH, 'F');
      }
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...DARK);
      doc.text(r.route.replace('\u2192', '>'), cols[0].x, y + 5.5);
      const cell = (x, st, extra) => {
        const label = baggageStatusLabel(st) || extra || '-';
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...statusColor(st));
        doc.text(extra && st ? `${label} (${extra})` : label, x, y + 5.5);
      };
      doc.setFontSize(8.5);
      cell(cols[1].x, r.personal);
      cell(cols[2].x, r.cabin);
      cell(cols[3].x, r.checked, r.allowance);
      cell(cols[4].x, r.checked2);
      y += rowH;
    });
    doc.setDrawColor(...LINE);
    doc.roundedRect(MARGIN, bagTop, CONTENT_W, y - bagTop, 1.5, 1.5, 'S');
    y += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text(tr('bagNote'), MARGIN, y);
    y += 8;
  } else {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...BODY);
    const lines = doc.splitTextToSize(tr('bagUnavailable'), CONTENT_W);
    doc.text(lines, MARGIN, y + 1);
    y += lines.length * 4.5 + 6;
  }

  // ---- Fare conditions (only what the airline's fare rules actually state) ----
  if (conditions.length) {
    ensureSpace(14 + conditions.length * 8);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...DARK);
    doc.text(tr('bagFareTitle'), MARGIN, y);
    y += 6;
    const condTop = y;
    conditions.forEach((c, i) => {
      const rowH = 8;
      if (i % 2 === 0) {
        doc.setFillColor(...PANEL);
        doc.rect(MARGIN, y, CONTENT_W, rowH, 'F');
      }
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(...DARK);
      doc.text(c.label, MARGIN + 4, y + 5.3);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(...(c.status === 'inc' ? GOOD : c.status === 'cha' ? [181, 71, 8] : [180, 35, 24]));
      doc.text(conditionStatusLabel(c.status), MARGIN + CONTENT_W - 4, y + 5.3, { align: 'right' });
      y += rowH;
    });
    doc.setDrawColor(...LINE);
    doc.roundedRect(MARGIN, condTop, CONTENT_W, y - condTop, 1.5, 1.5, 'S');
    y += 9;
  }

  // ---- Total ----
  const totalH = seatFees > 0 ? 20 : 14;
  ensureSpace(totalH + 2);
  card(MARGIN, y, CONTENT_W, totalH, PANEL);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...DARK);
  doc.text(tr('totalPaid'), MARGIN + 5, y + 9);
  doc.setTextColor(...BRAND);
  doc.setFontSize(13);
  doc.text(formatMoney(total, currency), MARGIN + CONTENT_W - 5, y + 9.3, { align: 'right' });
  if (seatFees > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    doc.text(`${tr('seatsFee')} ${formatMoney(seatFees, currency)}`, MARGIN + CONTENT_W - 5, y + 16, { align: 'right' });
  }
  y += totalH;

  // ---- Footer + accent bars on every page ----
  const footerY = 280;
  const pages = doc.getNumberOfPages();
  for (let n = 1; n <= pages; n++) {
    doc.setPage(n);
    if (n > 1) {
      doc.setFillColor(...BRAND);
      doc.rect(0, 0, PAGE_W, 3, 'F');
    }
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, footerY - 6, MARGIN + CONTENT_W, footerY - 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(tr('pdfQuestions'), MARGIN, footerY);
    doc.text(tr('pdfArrive'), MARGIN, footerY + 4);
    if (pages > 1) doc.text(`${n} / ${pages}`, MARGIN + CONTENT_W, footerY, { align: 'right' });
    doc.setFillColor(...BRAND);
    doc.rect(0, 294, PAGE_W, 3, 'F');
  }

  doc.save(`flyafritrip-ticket-${tripDetails.reference_number}.pdf`);
}
